import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface VimeoFolder {
  uri: string;
  name: string;
  description?: string;
  created_time: string;
  modified_time: string;
  resource_key: string;
  metadata: {
    connections: {
      videos: {
        uri: string;
        total: number;
      };
    };
  };
}

export interface VimeoVideo {
  uri: string;
  name: string;
  description?: string;
  created_time: string;
  duration: number;
  link: string;
  embed: {
    html: string;
  };
  files: Array<{
    quality: string;
    type: string;
    link: string;
    fps: number;
    size: number;
  }>;
  pictures: {
    base_link: string;
  };
  player_embed_url: string;
}

@Injectable({
  providedIn: 'root'
})
export class VimeoService {
  private readonly baseUrl = 'https://api.vimeo.com';
  private readonly headers = new HttpHeaders({
    'Authorization': `Bearer ${environment.vimeo.accessToken}`,
    'Content-Type': 'application/json',
    'Accept': 'application/vnd.vimeo.*+json;version=3.4'
  });

  constructor(private http: HttpClient) {}

  /**
   * Crear una carpeta en Vimeo
   */
  createFolder(name: string, description?: string): Observable<VimeoFolder> {
    const body = {
      name: name,
      description: description || ''
    };

    return this.http.post<VimeoFolder>(`${this.baseUrl}/me/projects`, body, {
      headers: this.headers
    });
  }

  /**
   * Obtener todas las carpetas del usuario
   */
  getFolders(): Observable<{ data: VimeoFolder[] }> {
    return this.http.get<{ data: VimeoFolder[] }>(`${this.baseUrl}/me/projects`, {
      headers: this.headers
    });
  }

  /**
   * Obtener videos de una carpeta específica
   */
  getVideosFromFolder(folderId: string): Observable<{ data: VimeoVideo[] }> {
    return this.http.get<{ data: VimeoVideo[] }>(`${this.baseUrl}/me/projects/${folderId}/videos`, {
      headers: this.headers
    });
  }

  /**
   * Subir video a Vimeo
   */
  uploadVideoToVimeo(file: File, name: string, description?: string): Observable<any> {
    return new Observable(observer => {
      // Primer paso: crear el video entry en Vimeo
      const createBody = {
        upload: {
          approach: 'tus',
          size: file.size
        },
        name: name,
        description: description || '',
        // ✅ OPTIMIZACIÓN: Configuración para archivos grandes
        privacy: {
          view: 'unlisted' // Comenzar como no listado para procesamiento más rápido
        }
      };

      this.http.post(`${this.baseUrl}/me/videos`, createBody, {
        headers: this.headers
      }).subscribe({
        next: (response: any) => {
          const uploadLink = response.upload.upload_link;
          const videoUri = response.uri;
          
          // Segundo paso: subir el archivo usando TUS
          this.uploadFileWithTus(file, uploadLink).subscribe({
            next: (progress) => {
              if (progress.type === 'progress') {
                observer.next({ 
                  type: 'progress', 
                  loaded: progress.loaded, 
                  total: progress.total 
                });
              } else if (progress.type === 'complete') {
                observer.next({ 
                  type: 'complete', 
                  videoUri: videoUri,
                  embedUrl: `https://player.vimeo.com/video/${videoUri.split('/').pop()}`
                });
                observer.complete();
              }
            },
            error: (error) => observer.error(error)
          });
        },
        error: (error) => observer.error(error)
      });
    });
  }

  /**
   * Añadir video a una carpeta
   */
  addVideoToFolder(folderId: string, videoUri: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/me/projects/${folderId}${videoUri}`, {}, {
      headers: this.headers
    });
  }

  /**
   * Eliminar video de Vimeo
   */
  deleteVideo(videoUri: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}${videoUri}`, {
      headers: this.headers
    });
  }

  /**
   * Eliminar un video por ID (atajo para DELETE /videos/{video_id})
   * Asegúrate de que el token tenga permisos para borrar (scopes adecuados)
   */
  deleteVideoById(videoId: string | number): Observable<void> {
    const id = String(videoId).trim();
    return this.http.delete<void>(`${this.baseUrl}/videos/${id}`, {
      headers: this.headers
    });
  }

  /**
   * Obtener información de un video específico
   */
  getVideoInfo(videoUri: string): Observable<VimeoVideo> {
    return this.http.get<VimeoVideo>(`${this.baseUrl}${videoUri}`, {
      headers: this.headers
    });
  }

  /**
   * Actualizar metadatos de un video (nombre y/o descripción)
   * Acepta un ID de Vimeo ("123456789") o un URI ("/videos/123456789")
   */
  updateVideoMetadata(videoIdOrUri: string, name?: string, description?: string): Observable<VimeoVideo> {
    if (!videoIdOrUri) {
      // Lanzar un error observable si no hay id
      return new Observable(observer => {
        observer.error(new Error('videoIdOrUri es requerido'));
      });
    }

    const id = videoIdOrUri.includes('/videos/')
      ? (videoIdOrUri.split('/').pop() || videoIdOrUri)
      : videoIdOrUri;

    const body: any = {};
    if (typeof name === 'string') body.name = name;
    if (typeof description === 'string') body.description = description;

    return this.http.patch<VimeoVideo>(`${this.baseUrl}/videos/${id}`, body, {
      headers: this.headers
    });
  }

  /**
   * Subir una miniatura personalizada para un video y activarla.
   * Acepta un ID de Vimeo ("123456789") o un URI ("/videos/123456789").
   */
  uploadCustomThumbnail(videoIdOrUri: string, file: File): Observable<void> {
    if (!videoIdOrUri || !file) {
      return new Observable(observer => observer.error(new Error('videoIdOrUri y file son requeridos')));
    }

    const id = videoIdOrUri.includes('/videos/')
      ? (videoIdOrUri.split('/').pop() || videoIdOrUri)
      : videoIdOrUri;

    // 1) Crear recurso de picture para "custom"
    return this.http.post<any>(`${this.baseUrl}/videos/${id}/pictures`, { type: 'custom' }, {
      headers: this.headers
    }).pipe(
      switchMap((res: any) => {
        const uploadLink = res?.upload?.upload_link || res?.upload?.link || res?.link;
        const pictureUri = res?.uri; // e.g., /videos/{id}/pictures/{picture_id}
        if (!uploadLink || !pictureUri) {
          throw new Error('Respuesta de Vimeo inválida al crear picture');
        }

        // 2) Subir archivo binario a uploadLink (sin Authorization)
        const imgHeaders = new HttpHeaders({ 'Content-Type': file.type || 'image/jpeg' });
        return this.http.put(uploadLink, file, {
          headers: imgHeaders,
          observe: 'response',
          responseType: 'text' as 'json'
        }).pipe(map(() => pictureUri));
      }),
      // 3) Activar la miniatura
      switchMap((pictureUri: string) => this.http.patch<void>(`${this.baseUrl}${pictureUri}`, { active: true }, {
        headers: this.headers
      }))
    );
  }

  /**
   * Subir archivo usando protocolo TUS (optimizado para archivos grandes hasta 10GB)
   */
  private uploadFileWithTus(file: File, uploadLink: string): Observable<any> {
    return new Observable(observer => {
      // Tamaño de chunk adaptado: Safari/iOS prefieren chunks más pequeños
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || /iPad|iPhone|iPod/.test(navigator.userAgent);
      let chunkSize = isSafari ? 8 * 1024 * 1024 : 16 * 1024 * 1024; // 8MB Safari / 16MB otros
      try { console.debug(`[UPLOAD] ChunkSize=${(chunkSize / (1024*1024)).toFixed(0)}MB (Safari=${isSafari})`); } catch {}

      let offset = 0;
      let attempts = 0;
      const maxRetries = 3;

      const headHeaders = new HttpHeaders({ 'Tus-Resumable': '1.0.0' });
      const getServerOffset = () => this.http.head(uploadLink, {
        headers: headHeaders,
        observe: 'response',
        responseType: 'text' as 'json'
      });

      const uploadChunk = () => {
        const end = Math.min(offset + chunkSize, file.size);
        const chunk = file.slice(offset, end);
        const headers = new HttpHeaders({
          'Tus-Resumable': '1.0.0',
          'Upload-Offset': offset.toString(),
          'Content-Type': 'application/offset+octet-stream'
        });

        const sub = this.http.patch(uploadLink, chunk, {
          headers,
          observe: 'events',
          reportProgress: true,
          responseType: 'text' as 'json'
        }).subscribe({
          next: (event: HttpEvent<any>) => {
            if (event.type === HttpEventType.UploadProgress) {
              const totalLoaded = offset + (event.loaded || 0);
              observer.next({ type: 'progress', loaded: totalLoaded, total: file.size });
            } else if (event.type === HttpEventType.Response) {
              // Confiar en el offset reportado por el servidor si está disponible
              const serverOffsetStr = (event as any)?.headers?.get?.('Upload-Offset') || (event as any)?.headers?.get?.('upload-offset');
              const serverOffset = serverOffsetStr ? parseInt(serverOffsetStr, 10) : NaN;
              if (!isNaN(serverOffset)) {
                offset = serverOffset;
              } else {
                offset = end; // fallback conservador
              }
              attempts = 0; // reset de reintentos al completar un chunk

              if (offset >= file.size) {
                observer.next({ type: 'complete' });
                observer.complete();
              } else {
                // Opcional: ajuste simple de chunk según progreso (más pequeño si conexión lenta)
                // NOTA: Mantener simple para estabilidad
                uploadChunk();
              }
            }
          },
          error: async (error) => {
            try { sub.unsubscribe(); } catch {}
            attempts++;
            if (attempts <= maxRetries) {
              // Intentar reanudar preguntando el offset actual al servidor
              try {
                const res = await firstValueFrom(getServerOffset());
                const offStr = res.headers.get('Upload-Offset') || res.headers.get('upload-offset');
                const off = offStr ? parseInt(offStr, 10) : NaN;
                if (!isNaN(off) && off >= 0 && off <= file.size) {
                  offset = off;
                }
              } catch {}
              const backoff = attempts * 500; // 0.5s, 1s, 1.5s
              setTimeout(uploadChunk, backoff);
            } else {
              console.error('❌ Error en chunk upload (máx reintentos):', error);
              observer.error(error);
            }
          }
        });
      };

      // Intentar recuperar offset actual antes de iniciar (reanudar subidas interrumpidas)
      (async () => {
        try {
          const res = await firstValueFrom(getServerOffset());
          const offStr = res.headers.get('Upload-Offset') || res.headers.get('upload-offset');
          const off = offStr ? parseInt(offStr, 10) : NaN;
          if (!isNaN(off) && off > 0) {
            offset = off;
          }
        } catch {}
        uploadChunk();
      })();
    });
  }

  /**
   * Extraer ID de video desde URI de Vimeo
   */
  extractVideoId(uri: string): string {
    return uri.split('/').pop() || '';
  }

  /**
   * Generar URL de embed para un video
   */
  generateEmbedUrl(videoId: string): string {
    return `https://player.vimeo.com/video/${videoId}`;
  }
}
